import React, { useState } from 'react';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, Button
} from '@mui/material';
import { Lock as LockIcon, Edit as EditIcon, Warning as WarningIcon } from '@mui/icons-material';
import ESocialReferenceAutocomplete from './ESocialReferenceAutocomplete';
import type { S2210Metadata } from '../../../../../types/esocial-forms';

interface Props {
  metadata: Partial<S2210Metadata> | null;
  onChange: (metadata: Partial<S2210Metadata>) => void;
  gaps?: string[];
  overrideEnabled?: boolean;
}

const UF_OPTIONS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const S2210Form: React.FC<Props> = ({ metadata, onChange, gaps = [], overrideEnabled = false }) => {
  const [isOverride, setIsOverride] = useState(false);
  const cat = metadata?.cat ?? { dtAcid: '', tpAcid: 1, tpCat: 1, iniciatCAT: 1 };

  const updateCat = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      cat: { ...cat, [field]: value }
    } as Partial<S2210Metadata>);
  };

  const updateLocalAcidente = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      cat: { ...cat, localAcidente: { ...cat.localAcidente, tpLocal: cat.localAcidente?.tpLocal ?? 1, [field]: value } }
    } as Partial<S2210Metadata>);
  };

  const updateParteAtingida = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      cat: { ...cat, parteAtingida: { ...cat.parteAtingida, codParteAting: cat.parteAtingida?.codParteAting ?? '', lateralidade: cat.parteAtingida?.lateralidade ?? 0, [field]: value } }
    } as Partial<S2210Metadata>);
  };

  const updateAtestado = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      cat: { ...cat, atestado: { ...cat.atestado, dtAtend: cat.atestado?.dtAtend ?? '', indInternacao: cat.atestado?.indInternacao ?? 'N', durTrat: cat.atestado?.durTrat ?? 0, indAfast: cat.atestado?.indAfast ?? 'N', codCID: cat.atestado?.codCID ?? '', [field]: value } }
    } as Partial<S2210Metadata>);
  };

  const updateEmitente = (field: string, value: unknown) => {
    const atestado = cat.atestado ?? { dtAtend: '', indInternacao: 'N', durTrat: 0, indAfast: 'N', codCID: '' };
    onChange({
      ...metadata,
      cat: { ...cat, atestado: { ...atestado, emitente: { ...atestado.emitente, nmEmit: atestado.emitente?.nmEmit ?? '', ideOC: atestado.emitente?.ideOC ?? 1, nrOC: atestado.emitente?.nrOC ?? '', [field]: value } } }
    } as Partial<S2210Metadata>);
  };

  return (
    <Box>
      {/* Review mode header */}
      {gaps.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main">
            {gaps.length} campo(s) pendente(s) de preenchimento
          </Typography>
          {overrideEnabled && (
            <Button
              size="small"
              variant={isOverride ? 'contained' : 'outlined'}
              startIcon={isOverride ? <LockIcon /> : <EditIcon />}
              onClick={() => setIsOverride(!isOverride)}
              sx={{ ml: 'auto' }}
            >
              {isOverride ? 'Bloquear campos' : 'Editar todos os campos'}
            </Button>
          )}
        </Box>
      )}

      {/* Secao: Acidente */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Dados do Acidente</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth type="date" label="Data do Acidente *" value={cat.dtAcid || ''} onChange={(e) => updateCat('dtAcid', e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth type="time" label="Hora do Acidente" value={cat.hrAcid || ''} onChange={(e) => updateCat('hrAcid', e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Horas Trabalhadas Antes" value={cat.hrsTrabAntesAcid || ''} onChange={(e) => updateCat('hrsTrabAntesAcid', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Acidente *</InputLabel>
            <Select value={cat.tpAcid ?? 1} onChange={(e) => updateCat('tpAcid', e.target.value)} label="Tipo de Acidente *">
              <MenuItem value={1}>Tipico</MenuItem>
              <MenuItem value={2}>Trajeto</MenuItem>
              <MenuItem value={3}>Doenca Ocupacional</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Tipo de CAT *</InputLabel>
            <Select value={cat.tpCat ?? 1} onChange={(e) => updateCat('tpCat', e.target.value)} label="Tipo de CAT *">
              <MenuItem value={1}>Inicial</MenuItem>
              <MenuItem value={2}>Reabertura</MenuItem>
              <MenuItem value={3}>Comunicacao de Obito</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Iniciativa da CAT *</InputLabel>
            <Select value={cat.iniciatCAT ?? 1} onChange={(e) => updateCat('iniciatCAT', e.target.value)} label="Iniciativa da CAT *">
              <MenuItem value={1}>Empregador</MenuItem>
              <MenuItem value={2}>Sindicato</MenuItem>
              <MenuItem value={3}>Medico</MenuItem>
              <MenuItem value={4}>Autoridade Publica</MenuItem>
              <MenuItem value={5}>Dependente</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <ESocialReferenceAutocomplete tableNumber={15} value={cat.codSitGeradora || ''} onChange={(code) => updateCat('codSitGeradora', code)} label="Situacao Geradora (Tab. 15)" size="small" />
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={2} label="Observacoes da CAT" value={cat.obsCAT || ''} onChange={(e) => updateCat('obsCAT', e.target.value)} />
        </Grid>
      </Grid>

      {/* Secao: Local do Acidente */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Local do Acidente</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Local</InputLabel>
            <Select value={cat.localAcidente?.tpLocal ?? 1} onChange={(e) => updateLocalAcidente('tpLocal', e.target.value)} label="Tipo de Local">
              <MenuItem value={1}>Estabelecimento do empregador</MenuItem>
              <MenuItem value={2}>Empresa terceira</MenuItem>
              <MenuItem value={3}>Via publica</MenuItem>
              <MenuItem value={4}>Area rural</MenuItem>
              <MenuItem value={5}>Embarcacao</MenuItem>
              <MenuItem value={6}>Outros</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth label="Descricao do Local" value={cat.localAcidente?.dscLocal || ''} onChange={(e) => updateLocalAcidente('dscLocal', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label="Logradouro" value={cat.localAcidente?.dscLograd || ''} onChange={(e) => updateLocalAcidente('dscLograd', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <TextField fullWidth label="Numero" value={cat.localAcidente?.nrLograd || ''} onChange={(e) => updateLocalAcidente('nrLograd', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Bairro" value={cat.localAcidente?.bairro || ''} onChange={(e) => updateLocalAcidente('bairro', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField fullWidth label="CEP" value={cat.localAcidente?.cep || ''} onChange={(e) => updateLocalAcidente('cep', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select value={cat.localAcidente?.uf || ''} onChange={(e) => updateLocalAcidente('uf', e.target.value)} label="UF">
              <MenuItem value="">-</MenuItem>
              {UF_OPTIONS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Secao: Lesao */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Lesao</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ESocialReferenceAutocomplete tableNumber={13} value={cat.parteAtingida?.codParteAting || ''} onChange={(code) => updateParteAtingida('codParteAting', code)} label="Parte do Corpo Atingida (Tab. 13) *" required size="small" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Lateralidade</InputLabel>
            <Select value={cat.parteAtingida?.lateralidade ?? 0} onChange={(e) => updateParteAtingida('lateralidade', e.target.value)} label="Lateralidade">
              <MenuItem value={0}>Nao aplicavel</MenuItem>
              <MenuItem value={1}>Esquerda</MenuItem>
              <MenuItem value={2}>Direita</MenuItem>
              <MenuItem value={3}>Ambas</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <ESocialReferenceAutocomplete tableNumber={14} value={cat.agenteCausador?.codAgntCausador || ''} onChange={(code) => updateCat('agenteCausador', { codAgntCausador: code })} label="Agente Causador do Acidente (Tab. 14) *" required size="small" />
        </Grid>
      </Grid>

      {/* Secao: Atestado Medico */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Atestado Medico</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth type="date" label="Data do Atendimento *" value={cat.atestado?.dtAtend || ''} onChange={(e) => updateAtestado('dtAtend', e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Houve Internacao? *</InputLabel>
            <Select value={cat.atestado?.indInternacao ?? 'N'} onChange={(e) => updateAtestado('indInternacao', e.target.value)} label="Houve Internacao? *">
              <MenuItem value="S">Sim</MenuItem>
              <MenuItem value="N">Nao</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth type="number" label="Duracao do Tratamento (dias)" value={cat.atestado?.durTrat ?? 0} onChange={(e) => updateAtestado('durTrat', parseInt(e.target.value) || 0)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Houve Afastamento? *</InputLabel>
            <Select value={cat.atestado?.indAfast ?? 'N'} onChange={(e) => updateAtestado('indAfast', e.target.value)} label="Houve Afastamento? *">
              <MenuItem value="S">Sim</MenuItem>
              <MenuItem value="N">Nao</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Codigo CID-10 *" value={cat.atestado?.codCID || ''} onChange={(e) => updateAtestado('codCID', e.target.value)} helperText="Ex: S62.0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Diagnostico Provavel" value={cat.atestado?.diagProvavel || ''} onChange={(e) => updateAtestado('diagProvavel', e.target.value)} />
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={2} label="Descricao da Lesao" value={cat.atestado?.dscLesao || ''} onChange={(e) => updateAtestado('dscLesao', e.target.value)} />
        </Grid>

        {/* Emitente */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mt: 1, mb: 0.5, fontWeight: 500 }}>Medico Emitente</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Nome do Medico" value={cat.atestado?.emitente?.nmEmit || ''} onChange={(e) => updateEmitente('nmEmit', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Orgao de Classe</InputLabel>
            <Select value={cat.atestado?.emitente?.ideOC ?? 1} onChange={(e) => updateEmitente('ideOC', e.target.value)} label="Orgao de Classe">
              <MenuItem value={1}>CRM</MenuItem>
              <MenuItem value={2}>CRO</MenuItem>
              <MenuItem value={3}>RMS</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField fullWidth label="Numero do Registro" value={cat.atestado?.emitente?.nrOC || ''} onChange={(e) => updateEmitente('nrOC', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select value={cat.atestado?.emitente?.ufOC || ''} onChange={(e) => updateEmitente('ufOC', e.target.value as string)} label="UF">
              <MenuItem value="">-</MenuItem>
              {UF_OPTIONS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default S2210Form;
